"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { LIVE_STATUSES } from "@/contants";
import { formatUnixToVNDate, formatHandicapLabel } from "@/utils/format";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";
import ICON_CORNER from "@/assets/images/icon-corner.svg";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import OddsMatchCell from "./KeoOddsMatchCell";
import OddsMatchTime from "./KeoOddsMatchTime";
import OddsMatchOuCell from "./KeoOddsMatchOuCell";
import OddsMatch1x2Cell from "./KeoOddsMatch1x2Cell";
import OrthofitOtherBetsDropdown from "../OrthofitOtherBetsDropdown";

export default function KeoOddsMatchRow({ match, matchIndex = 0 }) {
  const { getMatchOdds } = useMatches();
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const [openOtherOdds, setOpenOtherOdds] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const companyIdOdds = useSelector((state) => state.bookmakerReducer);

  const liveScore = getScore();
  const liveOdds = getOdds();
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);
  const { currentHomeScore, currentAwayScore, currentHomeCorner, currentAwayCorner } = useMatchData(liveScore, match);
  const odds = useMatchOdds(match, filterOdds, companyIdOdds);

  const statusNum = liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const { date: formattedDate, gio: formattedGio } = formatUnixToVNDate(match?.matchTime);

  const handleOtherOddsClick = () => {
    setOpenOtherOdds((prev) => !prev);
    if (!openOtherOdds && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => { if (data?.success) setOtherOddsData(data.data); })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds) : [];
  const correctScoresListHalf = otherOddsData?.correctScoresHalfOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds) : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const correctScoresHalf = splitCorrectScoresListToSections(correctScoresListHalf);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds) ? otherOddsData.totalCornersHalfOdds : [];
  const goalOddEvenFirst = goalOddEvenOdds?.[0] ?? {};
  const hasOtherData = goalOddEvenOdds?.length > 0 || correctScoresListFull.length > 0 || totalCornersOdds.length > 0 || correctScoresListHalf.length > 0 || totalCornersHalfOdds.length > 0;

  const oddsPeriod = useSelector((state) => state.oddsPeriodReducer) || "full";
  const isHalf1 = oddsPeriod === "half1";
  const gridColumnsHalf1 = "75px 55px 40px 1.2fr 2fr 60px";
  const gridColumnsFull = "75px 55px 40px 1.2fr 1.8fr 1.8fr 60px";

  return (
    <div className={`kbv-match-row flex flex-col w-full border-b [border-bottom-style:solid] border-[var(--theme-border)] transition-colors ${isLive ? 'kbv-row--live' : (matchIndex % 2 === 0 ? 'kbv-row--even' : 'kbv-row--odd')}`}>
      <div
        className="kbv-grid-row hidden md:grid w-full min-h-[110px] border-b [border-bottom-style:solid] border-[var(--theme-border)]"
        style={{ gridTemplateColumns: isHalf1 ? gridColumnsHalf1 : gridColumnsFull }}
      >
        {/* Time */}
        <div className="border-r [border-right-style:solid] border-[var(--theme-border)] h-full flex items-center justify-center">
          <OddsMatchTime
            match={match} isLive={isLive} formattedDate={formattedDate} formattedGio={formattedGio}
            liveScore={liveScore} statusNum={statusNum}
          />
        </div>
        {/* Score */}
        <div className="flex items-center justify-center border-r [border-right-style:solid] border-[var(--theme-border)] h-full">
          <span className="kbv-score text-base">{isLive ? `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}` : '-'}</span>
        </div>
        {/* Corner */}
        <div className="flex items-center justify-center border-r [border-right-style:solid] border-[var(--theme-border)] h-full">
          <span className="kbv-corner-score text-xs">{isLive ? `${currentHomeCorner ?? 0} - ${currentAwayCorner ?? 0}` : '-'}</span>
        </div>
        {/* Teams */}
        <div className="flex flex-col justify-center px-1 py-1 min-w-0 border-r [border-right-style:solid] border-[var(--theme-border)] h-full overflow-hidden">
          <div className="flex items-center gap-1 py-0.5 min-w-0">
            <img src={match.homeTeam?.logo || match.homeLogo} alt="" className="w-[18px] h-[18px] object-contain shrink-0" loading="lazy" />
            <span className={`text-sm font-medium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[color:var(--kbv-team-name-color)] ${currentHomeScore > currentAwayScore ? "font-bold" : ""}`}>{ match?.homeTeam?.nameVi || match.homeName}</span>
          </div>
          <div className="flex items-center gap-1 py-0.5 min-w-0">
            <img src={match.awayTeam?.logo || match.awayLogo} alt="" className="size-[18px] object-contain shrink-0" loading="lazy" />
            <span className={`text-sm font-medium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[color:var(--kbv-team-name-color)] ${currentAwayScore > currentHomeScore ? "font-bold" : ""}`}>{ match?.awayTeam?.nameVi || match.awayName}</span>
          </div>
        </div>

        {/* HT odds (Hiệp 1) */}
        <div className="grid grid-cols-3 min-w-0 border-r [border-right-style:solid] border-[var(--theme-border)] h-full py-[9px]">
          <div className="flex flex-col justify-center gap-2.5 h-full px-1 min-w-0 overflow-hidden">
            <OddsMatchCell label={formatHandicapLabel(odds.handicapHalfValue, true)} value={odds.homeHandicapHalfOdds} trend={odds.homeHandicapHalfTrend} />
            <OddsMatchCell label="" value={odds.awayHandicapHalfOdds} trend={odds.awayHandicapHalfTrend} />
            <OddsMatchCell label="" value="-" />
          </div>
          <div className="flex flex-col justify-center gap-2.5 h-full px-1 min-w-0 overflow-hidden">
            <OddsMatchOuCell line={odds.overUnderHalfValue} side="O" odds={odds.overHalfOdds} trend={odds.overHalfTrend} />
            <OddsMatchOuCell line={odds.overUnderHalfValue} side="U" odds={odds.underHalfOdds} trend={odds.underHalfTrend} />
            <div className="h-5" />
          </div>
          <div className="flex flex-col justify-center gap-2.5 h-full px-1 min-w-0 overflow-hidden">
            <OddsMatch1x2Cell value={odds.homeEuropeHalfOdds} trend={odds.homeEuropeHalfTrend} />
            <OddsMatch1x2Cell value={odds.awayEuropeHalfOdds} trend={odds.awayEuropeHalfTrend} />
            <OddsMatch1x2Cell value={odds.drawHalfOdds} trend={odds.drawHalfTrend} />
          </div>
        </div>

        {/* FT odds (Toàn trận) - ẩn khi chọn Hiệp 1 */}
        {!isHalf1 && (
          <div className="grid grid-cols-3 min-w-0 border-r [border-right-style:solid] border-[var(--theme-border)] h-full py-[9px]">
            <div className="flex flex-col justify-center gap-2.5 h-full px-1 min-w-0 overflow-hidden">
              <OddsMatchCell label={formatHandicapLabel(odds.handicapValue, true)} value={odds.homeHandicapOdds} trend={odds.homeHandicapTrend} />
              <OddsMatchCell label="" value={odds.awayHandicapOdds} trend={odds.awayHandicapTrend} />
              <OddsMatchCell label="" value="-" />
            </div>
            <div className="flex flex-col justify-center gap-2.5 h-full px-1 min-w-0 overflow-hidden">
              <OddsMatchOuCell line={odds.overUnderValue} side="O" odds={odds.overOdds} trend={odds.overTrend} />
              <OddsMatchOuCell line={odds.overUnderValue} side="U" odds={odds.underOdds} trend={odds.underTrend} />
              <div className="h-5" />
            </div>
            <div className="flex flex-col justify-center gap-2.5 h-full px-1 min-w-0 overflow-hidden">
              <OddsMatch1x2Cell value={odds.homeEuropeOdds} trend={odds.homeEuropeTrend} />
              <OddsMatch1x2Cell value={odds.awayEuropeOdds} trend={odds.awayEuropeTrend} />
              <OddsMatch1x2Cell value={odds.instantDrawOdds} trend={odds.drawTrend} />
            </div>
          </div>
        )}

        {/* Other odds button column */}
        <div className="flex items-center justify-center h-full">
          <button
            type="button"
            onClick={handleOtherOddsClick}
            className={`kbv-keo-btn-v2 flex items-center justify-center rounded border border-solid border-[#d1d5db] w-8 h-8 text-[13px] font-medium transition-colors ${openOtherOdds ? "bg-[#E51B00] text-white border-[#E51B00]" : "bg-white text-[#333] hover:bg-gray-50"}`}
          >
            +{match.countOtherOdds ?? 0}
          </button>
        </div>
      </div>

      {/* Mobile */}
      <div className="flex md:hidden flex-col w-full">
        <div className="flex items-center justify-between px-3 py-1.5 border-b [border-bottom-style:solid] border-[var(--theme-border)]">
          <div className="flex items-center gap-2 shrink-0">
            <span className={`kbv-live-badge px-2 py-0.5 rounded text-[10px] font-bold ${isLive ? '' : 'invisible'}`}>
              Live
            </span>
          </div>
          <div className="kbv-mobile-time flex-1 text-center font-bold">
            {isLive ? (
              <>
                {match?.minute ?? statusNum ?? ""} <span className="blinking">'</span>
              </>
            ) : formattedGio}
          </div>
          <div className="flex items-center gap-1 font-bold shrink-0 whitespace-nowrap text-xs" style={{ color: 'var(--kbv-corner-score-color)' }}>
            <img src={ICON_CORNER} alt="corner" className="size-3" />
            <span>{currentHomeCorner ?? 0} - {currentAwayCorner ?? 0}</span>
          </div>
        </div>

        <div className="flex items-center px-3 py-2">
          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end pr-2">
            <span className="kbv-mobile-team-name overflow-hidden text-ellipsis font-bold text-right" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{match.homeName}</span>
            <img src={match.homeTeam?.logo || match.homeLogo} alt="" className="size-5 object-contain shrink-0" loading="lazy" />
          </div>

          <div className="kbv-mobile-score flex items-center justify-center gap-0.5 font-bold rounded-sm shrink-0 w-[54px] h-[18px] -ml-2.5">
            <span>{currentHomeScore ?? 0}</span>
            <span>-</span>
            <span>{currentAwayScore ?? 0}</span>
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-0 pl-2">
            <img src={match.awayTeam?.logo || match.awayLogo} alt="" className="size-5 object-contain shrink-0" loading="lazy" />
            <span className="kbv-mobile-team-name overflow-hidden text-ellipsis font-bold" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{match.awayName}</span>
          </div>
        </div>

        <div className="kbv-mobile-odds-header grid px-3 pb-1.5 text-center" style={{ gridTemplateColumns: '30% 30% 40%' }}>
          <div>Cược Chấp</div>
          <div>Tài/Xỉu</div>
          <div>1x2</div>
        </div>

        <div className="grid md:gap-2 gap-1 px-3 pb-3 min-w-0" style={{ gridTemplateColumns: '30% 30% 40%' }}>
          <div className="flex flex-col gap-1 md:gap-2 min-w-0 overflow-hidden">
            <OddsMatchCell label={formatHandicapLabel(odds.handicapValue, true)} value={odds.homeHandicapOdds} trend={odds.homeHandicapTrend} />
            <OddsMatchCell label="" value={odds.awayHandicapOdds} trend={odds.awayHandicapTrend} />
          </div>

          <div className="flex flex-col gap-1 md:gap-2 min-w-0 overflow-hidden">
            <OddsMatchOuCell line={odds.overUnderValue} side="O" odds={odds.overOdds} trend={odds.overTrend} />
            <OddsMatchOuCell line={odds.overUnderValue} side="U" odds={odds.underOdds} trend={odds.underTrend} />
          </div>

          <div className="grid grid-cols-3 gap-1">
            <div className="flex flex-col items-center justify-center h-[45px] rounded kbv-odds-cell--normal">
              <div className="kbv-mobile-1x2-label">Chủ</div>
              <div className={`kbv-mobile-1x2-value relative inline-block ${odds.homeEuropeTrend === 'up' || odds.homeEuropeTrend === 'down' ? 'pr-3' : ''} ${odds.homeEuropeTrend === 'up' ? 'kbv-odds-value--up' : odds.homeEuropeTrend === 'down' ? 'kbv-odds-value--down' : ''}`}>{odds.homeEuropeOdds || "-"}</div>
            </div>
            <div className="flex flex-col items-center justify-center h-[45px] rounded kbv-odds-cell--normal">
              <div className="kbv-mobile-1x2-label">Khách</div>
              <div className={`kbv-mobile-1x2-value relative inline-block ${odds.awayEuropeTrend === 'up' || odds.awayEuropeTrend === 'down' ? 'pr-3' : ''} ${odds.awayEuropeTrend === 'up' ? 'kbv-odds-value--up' : odds.awayEuropeTrend === 'down' ? 'kbv-odds-value--down' : ''}`}>{odds.awayEuropeOdds || "-"}</div>
            </div>
            <div className="flex flex-col items-center justify-center h-[45px] rounded kbv-odds-cell--normal">
              <div className="kbv-mobile-1x2-label">Hòa</div>
              <div className={`kbv-mobile-1x2-value relative inline-block ${odds.drawTrend === 'up' || odds.drawTrend === 'down' ? 'pr-3' : ''} ${odds.drawTrend === 'up' ? 'kbv-odds-value--up' : odds.drawTrend === 'down' ? 'kbv-odds-value--down' : ''}`}>{odds.instantDrawOdds || "0"}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end pt-0.5 ">
          <button
            type="button"
            onClick={handleOtherOddsClick}
            className=" !text-black !rounded-none flex items-center justify-between pt-[5px] px-2 gap-1 text-xs cursor-pointer border-0 p-1 min-w-[100px]"
          >
            <span className={`${openOtherOdds ? "text-white" : "text-black"}`} >{match.countOtherOdds ?? 0} kèo</span>
            <img
              src={ICON_ARROW_UP}
              alt=""
              className={`size-4 transition-transform ${openOtherOdds ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {openOtherOdds && (
        <OrthofitOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          isLive={isLive}
          goalOddEvenOdds={goalOddEvenOdds}
          goalOddEvenFirst={goalOddEvenFirst}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          correctScoresListHalf={correctScoresListHalf}
          correctScoresHalf={correctScoresHalf}
          totalCornersOdds={totalCornersOdds}
          totalCornersHalfOdds={totalCornersHalfOdds}
          odds={odds}
          fontBody="text-[13px]"
        />
      )}
    </div>
  );
}
